/**
 * LegalAcceptanceBanner — thin top banner shown when the tenant has not
 * yet accepted the current mandatory legal documents.
 *
 * Suppressed for platform users. Fetches /api/legal/acceptance/status
 * once on mount. Does NOT block navigation — that is enforced by the
 * feature-gate in the fiscal / communications forms via
 * /api/legal/gated-features.
 */
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AlertTriangle, ArrowRight, X } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const DISMISS_KEY = "cx_legal_banner_dismissed";

export default function LegalAcceptanceBanner() {
  const { isPlatform, user } = useAuth();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (isPlatform) return;
    if (!user) return;
    // Only run on protected app routes (skip login/signup/marketing)
    const p = location.pathname;
    if (p === "/" || p.startsWith("/login") || p.startsWith("/signup") ||
        p.startsWith("/pricing") || p.startsWith("/faq") || p.startsWith("/contact") ||
        p.startsWith("/whatsapp-business-brevo") || p.startsWith("/restaurant") ||
        p.startsWith("/hotel") || p.startsWith("/combo") || p.startsWith("/enterprise") ||
        p.startsWith("/features")) {
      setVisible(false); return;
    }
    // Auto-dismiss session
    if (sessionStorage.getItem(DISMISS_KEY) === "1") { setDismissed(true); return; }

    (async () => {
      try {
        const t = localStorage.getItem("ccx_impersonation_token") ||
                  localStorage.getItem("ccx_access_token");
        if (!t) return;
        const r = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/legal/acceptance/status`,
          { headers: { Authorization: `Bearer ${t}` } }
        );
        if (!r.data?.all_mandatory_accepted) setVisible(true);
        else setVisible(false);
      } catch (_e) {
        // silent — banner is a soft nudge
      }
    })();
  }, [isPlatform, user, location.pathname]);

  if (!visible || dismissed) return null;
  if (location.pathname === "/settings/legal-documents") return null;

  return (
    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between gap-3 flex-wrap"
         data-testid="legal-acceptance-topbar-banner">
      <div className="flex items-start gap-2 min-w-0">
        <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
        <div className="text-[12.5px] text-amber-900 leading-snug">
          <strong>Documenti da accettare</strong> · Per usare funzioni fiscali e
          comunicazioni operative, devi leggere e accettare i documenti
          contrattuali, privacy e fiscali aggiornati.
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Link
          to="/settings/legal-documents"
          className="inline-flex items-center gap-1.5 h-7 px-3 rounded-md bg-amber-600 text-white text-[11.5px] font-semibold hover:bg-amber-700"
          data-testid="legal-topbar-cta"
        >
          Leggi e accetta <ArrowRight className="w-3 h-3" />
        </Link>
        <button
          type="button"
          onClick={() => { sessionStorage.setItem(DISMISS_KEY, "1"); setDismissed(true); }}
          className="p-1 rounded hover:bg-amber-100 text-amber-800"
          aria-label="Nascondi"
          data-testid="legal-topbar-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
